import React, { useRef, useState } from 'react'
import { Box, Stack, Fade, Typography, IconButton } from '@mui/material'
import LazyLoad from 'react-lazyload'
import { FormattedMessage } from 'react-intl'
import { PlayCircleOutline, PauseCircleOutline } from '@mui/icons-material'
import { Breakpoint } from '@mui/system'

interface IBlockFullImage1Col {
  image?: string
  preTitle?: string
  title?: string[]
  subtitle?: string[]
  titleColor?: string
  description?: string[]
  color?: string
  video?: string
  children?: React.ReactNode
  bgColor?: string
  logo?: string
  logoWidth?: Partial<Record<Breakpoint, number>>
  logoAlt?: string
  secondaryNav?: boolean
  videoOpacity?: boolean
  mb?: Partial<Record<Breakpoint, number>>
  dataGTM?: string
}

function styles(props: IBlockFullImage1Col) {
  return {
    boxStyle: {
      background: props.bgColor ?? '#111',
      position: 'relative',
      minHeight: { xs: '50dvh', sm: !props.secondaryNav ? 'calc(100dvh - 72px)' : 'calc(100dvh - 72px - 72px)' },
      height: 'auto'
    },
    imgContainerStyle: {
      position: 'absolute',
      top: 0,
      width: '100%',
      height: '100%',
      overflow: 'hidden'
    },
    imgStyle: {
      position: 'absolute',
      height: '100%',
      width: '100%',
      objectFit: 'cover',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0
    },
    videoPlayPauseStyle: {
      position: 'absolute',
      right: 8,
      bottom: 8,
      zIndex: 2
    },
    textContainerStyle: {
      position: 'relative',
      height: '100%',
      width: '100%',
      maxWidth: { xs: '100%', md: 900 },
      margin: 'auto',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      px: { xs: 3, sm: 8 },
      py: 8,
      mb: props.mb ?? 'auto',
      zIndex: 2
    },
    titleStyle: {
      color: props.titleColor ?? '#fff',
      fontWeight: 800,
      fontSize: { xs: '2.5rem', sm: '3rem', md: '4rem' },
      letterSpacing: -2,
      filter: 'drop-shadow(0px 0px 80px #000b)',
      zIndex: -1
    },
    subtitleStyle: {
      fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.3rem' },
      fontWeight: 500,
      color: props.titleColor ?? '#fffb',
      mt: 2
    },
    descriptionStyle: {
      fontSize: '1.2rem',
      fontWeight: 500,
      color: props.titleColor ?? '#fffb'
    },
    logoStyle: {
      width: props.logoWidth,
      maxWidth: '100%',
      mb: 3
    },
    childrenStyle: {
      flexDirection: { xs: 'column', sm: 'row' },
      gap: 2,
      mt: { xs: 2, sm: 4 },
      flexWrap: 'wrap',
      justifyContent: 'center'
    }
  }
}

const BlockFullImage1Col = (props: IBlockFullImage1Col) => {
  const {
    imgContainerStyle,
    imgStyle,
    videoPlayPauseStyle,
    textContainerStyle,
    titleStyle,
    descriptionStyle,
    childrenStyle,
    boxStyle,
    subtitleStyle,
    logoStyle
  } = styles(props)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(true)

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }
  return (
    <Stack component='header' sx={{ ...boxStyle }}>
      <Stack sx={{ ...textContainerStyle }}>
        {props.logo && <Box component='img' alt={props.logoAlt} sx={{ ...logoStyle }} src={props.logo} />}
        {props.preTitle && (
          <Typography sx={{ ...descriptionStyle }}>
            <FormattedMessage id={props.preTitle} />
          </Typography>
        )}
        {props.title &&
          props.title.map((item: string, index: number) => (
            <Typography variant='h1' sx={{ ...titleStyle }} key={index}>
              <FormattedMessage id={item} values={{ br: <br /> }} />
            </Typography>
          ))}
        {props.subtitle &&
          props.subtitle.map((item: string, index: number) => (
            <Typography component='h2' sx={{ ...subtitleStyle }} key={index}>
              <FormattedMessage id={item} />
            </Typography>
          ))}
        {props.description &&
          props.description.map((item: string, index: number) => (
            <Typography sx={{ ...descriptionStyle }} key={index}>
              <FormattedMessage id={item} />
            </Typography>
          ))}
        {props.children && (
          <Stack data-gtm-click-subsection1={props.dataGTM} sx={{ ...childrenStyle }}>
            {props.children}
          </Stack>
        )}
      </Stack>
      <LazyLoad height={700} offset={600}>
        <Fade in={true} timeout={1000}>
          <Box sx={{ ...imgContainerStyle }}>
            {props.video && (
              <>
                <Box
                  component='video'
                  src={props.video}
                  typeof='video/mp4'
                  autoPlay
                  preload='none'
                  loop
                  poster={props.image}
                  muted
                  ref={videoRef}
                  sx={{ ...imgStyle, zIndex: 1, opacity: props.videoOpacity ? 0.7 : 1 }}
                />
                <IconButton size='large' onClick={handlePlayPause} sx={{ ...videoPlayPauseStyle }}>
                  {isPlaying ? (
                    <PauseCircleOutline sx={{ fontSize: 32 }} />
                  ) : (
                    <PlayCircleOutline sx={{ fontSize: 32 }} />
                  )}
                </IconButton>
              </>
            )}
            {props.image && <Box component='img' sx={{ ...imgStyle }} src={props.image} />}
          </Box>
        </Fade>
      </LazyLoad>
    </Stack>
  )
}

export default BlockFullImage1Col
